// src/hooks/useAudioRecorder.ts
import { useCallback, useEffect, useRef, useState } from "react";
import type { AudioRecorderConfig, AudioRecorderState } from "../types";

// 目标采样率（与后端 ASR 一致）
const TARGET_SAMPLE_RATE = 16000;

function getUserMedia(): MediaDevices["getUserMedia"] | null {
    const md = navigator.mediaDevices;
    if (md && typeof md.getUserMedia === "function") {
        return md.getUserMedia.bind(md);
    }
    const legacy = navigator as Navigator & {
        webkitGetUserMedia?: typeof navigator.mediaDevices.getUserMedia;
        mozGetUserMedia?: typeof navigator.mediaDevices.getUserMedia;
    };
    const fn = legacy.webkitGetUserMedia || legacy.mozGetUserMedia;
    return fn ? fn.bind(navigator) : null;
}

function microphoneUnavailableReason(): string | null {
    if (typeof window !== "undefined" && window.isSecureContext === false) {
        return "当前页面不是 HTTPS，浏览器已禁用麦克风。请用 https 打开，或在本机使用 localhost。";
    }
    if (!getUserMedia()) {
        return "当前环境无法使用麦克风，请换用 Chrome / Safari，并允许麦克风权限。";
    }
    return null;
}

function formatGetUserMediaError(error: unknown): string {
    const name =
        error && typeof error === "object" && "name" in error
            ? String((error as { name?: string }).name)
            : "";
    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        return "麦克风权限被拒绝，请在浏览器设置中允许本站使用麦克风。";
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return "未检测到麦克风设备。";
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
        return "麦克风被其他程序占用，请关闭后重试。";
    }
    if (name === "SecurityError") {
        return "浏览器安全策略禁止录音，请使用 HTTPS 访问。";
    }
    if (error instanceof Error && error.message) return error.message;
    return "录音启动失败";
}

/**
 * 录制 raw PCM (16kHz / 16bit / mono) 录音器
 * - 使用 AudioContext + ScriptProcessorNode 抓取原始 PCM
 * - 实时重采样到 16kHz
 * - 输出 Blob (audio/pcm) 给上层转 base64 发送
 *
 * 不再使用 MediaRecorder，因为后者只能产出 webm/opus 容器，
 * 后端 ASR 期望 raw PCM，否则识别率几乎为 0。
 */
export const useAudioRecorder = (config?: AudioRecorderConfig) => {
    const [state, setState] = useState<AudioRecorderState>({
        isRecording: false,
        isProcessing: false,
        error: null,
        audioData: null,
        audioUrl: null,
        duration: 0,
        volume: 0,
    });

    // 用 ref 保存 stopRecording，避免 startRecording 的依赖循环
    const stopRecordingRef = useRef<() => Promise<Blob | null>>(() =>
        Promise.resolve(null),
    );

    const streamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const processorNodeRef = useRef<ScriptProcessorNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const animationFrameRef = useRef<number | null>(null);
    const timerRef = useRef<number | null>(null);
    const stopTimeoutRef = useRef<number | null>(null);
    const startTimeRef = useRef<number>(0);

    // 累积 PCM 样本（Int16）
    const pcmChunksRef = useRef<Int16Array[]>([]);
    const stopResolveRef = useRef<((blob: Blob | null) => void) | null>(null);
    const configRef = useRef(config);
    configRef.current = config;

    // ========== 释放录音相关资源 ==========
    const releaseRecordingResources = useCallback(() => {
        if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (processorNodeRef.current) {
            try {
                processorNodeRef.current.disconnect();
            } catch {
                /* ignore */
            }
            processorNodeRef.current = null;
        }
        if (sourceNodeRef.current) {
            try {
                sourceNodeRef.current.disconnect();
            } catch {
                /* ignore */
            }
            sourceNodeRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(() => {
                /* ignore */
            });
            audioContextRef.current = null;
        }
        analyserRef.current = null;
        if (stopTimeoutRef.current) {
            clearTimeout(stopTimeoutRef.current);
            stopTimeoutRef.current = null;
        }
    }, []);

    // ========== 设置音量检测（VAD 用时域 RMS 振幅） ==========
    const setupVolumeAnalyser = useCallback(() => {
        const bufferLength = analyserRef.current
            ? analyserRef.current.fftSize
            : 2048;
        const timeDataArray = new Uint8Array(bufferLength);

        const updateVolume = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getByteTimeDomainData(timeDataArray);

            // 计算 RMS（均方根）：直接反映时域振幅，比频域平均更可靠 VAD
            let sumSquares = 0;
            for (let i = 0; i < timeDataArray.length; i++) {
                const v = (timeDataArray[i] ?? 128) - 128; // 去直流偏置，范围 [-128, 127]
                sumSquares += v * v;
            }
            const rms = Math.sqrt(sumSquares / timeDataArray.length);
            // 归一化到 [0, 1]，除以 128 (最大可能值)
            const volume = Math.min(1, rms / 128);

            setState((prev) => ({ ...prev, volume }));
            const cfg = configRef.current;
            if (cfg?.onVolumeChange) {
                cfg.onVolumeChange(volume);
            }
            animationFrameRef.current = requestAnimationFrame(updateVolume);
        };
        updateVolume();
    }, []);

    // ========== 开始录音 ==========
    const startRecording = useCallback(async (): Promise<boolean> => {
        try {
            const blocked = microphoneUnavailableReason();
            if (blocked) {
                throw new Error(blocked);
            }
            const requestUserMedia = getUserMedia();
            if (!requestUserMedia) {
                throw new Error(microphoneUnavailableReason() || "无法访问麦克风");
            }
            if (audioContextRef.current) {
                console.warn("Already recording");
                return false;
            }

            setState((prev) => ({
                ...prev,
                isProcessing: true,
                error: null,
                duration: 0,
                volume: 0,
            }));

            const stream = await requestUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    sampleRate: TARGET_SAMPLE_RATE,
                },
            });
            streamRef.current = stream;

            const AudioContextClass =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext: typeof AudioContext })
                    .webkitAudioContext;
            const audioCtx = new AudioContextClass({
                sampleRate: TARGET_SAMPLE_RATE,
            });
            // 浏览器可能不接受 16kHz，按实际 sampleRate 处理
            await audioCtx.resume();
            audioContextRef.current = audioCtx;

            const source = audioCtx.createMediaStreamSource(stream);
            sourceNodeRef.current = source;

            // analyser 用于音量可视化 + VAD
            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 2048;
            analyser.smoothingTimeConstant = 0.3;
            source.connect(analyser);
            analyserRef.current = analyser;
            setupVolumeAnalyser();

            // ScriptProcessor：4096 帧、单声道
            const bufferLen = 4096;
            const processor = audioCtx.createScriptProcessor(
                bufferLen,
                1,
                1,
            );
            processorNodeRef.current = processor;

            const sourceSampleRate = audioCtx.sampleRate;
            const ratio = sourceSampleRate / TARGET_SAMPLE_RATE;

            processor.onaudioprocess = (e) => {
                if (!audioContextRef.current) return;
                const input = e.inputBuffer.getChannelData(0);
                const output = e.outputBuffer.getChannelData(0);
                output.fill(0);
                const targetLen = Math.floor(input.length / ratio);
                if (targetLen <= 0) return;
                const pcm = new Int16Array(targetLen);
                if (Math.abs(ratio - 1) < 0.001) {
                    for (let i = 0; i < targetLen; i++) {
                        const s = Math.max(-1, Math.min(1, input[i] ?? 0));
                        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
                    }
                } else {
                    for (let i = 0; i < targetLen; i++) {
                        const srcPos = i * ratio;
                        const i0 = Math.floor(srcPos);
                        const i1 = Math.min(i0 + 1, input.length - 1);
                        const frac = srcPos - i0;
                        const sample =
                            (input[i0] ?? 0) * (1 - frac) +
                            (input[i1] ?? 0) * frac;
                        const s = Math.max(-1, Math.min(1, sample));
                        pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
                    }
                }
                pcmChunksRef.current.push(pcm);
            };

            source.connect(processor);
            const mute = audioCtx.createGain();
            mute.gain.value = 0;
            processor.connect(mute);
            mute.connect(audioCtx.destination);

            pcmChunksRef.current = [];
            startTimeRef.current = Date.now();

            timerRef.current = window.setInterval(() => {
                setState((prev) => ({
                    ...prev,
                    duration: (Date.now() - startTimeRef.current) / 1000,
                }));
            }, 100);

            setState((prev) => ({
                ...prev,
                isRecording: true,
                isProcessing: false,
                error: null,
            }));

            if (config?.onStart) {
                config.onStart();
            }

            // 最大时长保护
            if (config?.maxDuration && config.maxDuration > 0) {
                stopTimeoutRef.current = window.setTimeout(() => {
                    if (audioContextRef.current) {
                        // 触发停止流程
                        void stopRecordingRef.current();
                    }
                }, config.maxDuration * 1000);
            }

            return true;
        } catch (error) {
            console.error("Failed to start recording:", error);
            const errorMessage = formatGetUserMediaError(error);
            setState((prev) => ({
                ...prev,
                isRecording: false,
                isProcessing: false,
                error: errorMessage,
            }));
            releaseRecordingResources();
            if (config?.onError) {
                config.onError(errorMessage);
            }
            return false;
        }
    }, [config, setupVolumeAnalyser, releaseRecordingResources]);

    // ========== 把累积的 Int16 chunks 拼成 PCM Blob ==========
    const buildPcmBlob = useCallback((): Blob => {
        const chunks = pcmChunksRef.current;
        let totalLen = 0;
        for (const c of chunks) totalLen += c.length;
        const merged = new Int16Array(totalLen);
        let offset = 0;
        for (const c of chunks) {
            merged.set(c, offset);
            offset += c.length;
        }
        // Int16Array → raw bytes
        const bytes = new Uint8Array(merged.buffer);
        return new Blob([bytes], { type: "audio/pcm" });
    }, []);

    // ========== 取走当前累积的 PCM（不停止录音），用于 VAD 按句切片 ==========
    // 连续监听模式下，检测到静音边界时调用：返回自上次 drain 以来的 PCM，
    // 并重置累加器，让后续采样进入新一句。录音本身继续运行。
    const drainPcmBlob = useCallback((): Blob => {
        const blob = buildPcmBlob();
        pcmChunksRef.current = [];
        return blob;
    }, [buildPcmBlob]);

    /** 只保留末尾 preroll，开口时丢掉长时间静音底噪 */
    const keepLastMs = useCallback((ms: number) => {
        const keep = Math.max(0, Math.round((TARGET_SAMPLE_RATE * ms) / 1000));
        const chunks = pcmChunksRef.current;
        let total = 0;
        for (const c of chunks) total += c.length;
        if (total <= keep) return;
        let need = keep;
        const kept: Int16Array[] = [];
        for (let i = chunks.length - 1; i >= 0 && need > 0; i--) {
            const c = chunks[i]!;
            if (c.length <= need) {
                kept.unshift(c);
                need -= c.length;
            } else {
                kept.unshift(c.subarray(c.length - need));
                need = 0;
            }
        }
        pcmChunksRef.current = kept;
    }, []);

    // ========== 停止录音并返回 Blob ==========
    const stopRecording = useCallback((): Promise<Blob | null> => {
        return new Promise<Blob | null>((resolve) => {
            if (!audioContextRef.current) {
                resolve(null);
                return;
            }
            stopResolveRef.current = resolve;
            setState((prev) => ({ ...prev, isProcessing: true }));

            if (stopTimeoutRef.current) {
                clearTimeout(stopTimeoutRef.current);
                stopTimeoutRef.current = null;
            }

            // 先摘除 onaudioprocess 避免新数据写入
            if (processorNodeRef.current) {
                processorNodeRef.current.onaudioprocess = null;
            }

            // 构造 Blob
            const blob = buildPcmBlob();
            const audioUrl = URL.createObjectURL(blob);

            setState((prev) => ({
                ...prev,
                isRecording: false,
                isProcessing: false,
                audioData: blob,
                audioUrl: audioUrl,
            }));

            const cfg = configRef.current;
            if (cfg?.onStop) {
                cfg.onStop(blob);
            }

            releaseRecordingResources();

            if (stopResolveRef.current) {
                stopResolveRef.current(blob);
                stopResolveRef.current = null;
            }
        });
    }, [buildPcmBlob, releaseRecordingResources]);

    // ========== 停止（简化版） ==========
    const stop = useCallback(() => {
        if (audioContextRef.current) {
            void stopRecording();
        }
    }, [stopRecording]);

    // 同步 stopRecording 到 ref（供 startRecording 的 maxDuration 回调使用，避免依赖循环）
    useEffect(() => {
        stopRecordingRef.current = stopRecording;
    }, [stopRecording]);

    // ========== 取消录音 ==========
    const cancelRecording = useCallback(() => {
        if (processorNodeRef.current) {
            processorNodeRef.current.onaudioprocess = null;
        }
        pcmChunksRef.current = [];
        stopResolveRef.current = null;
        releaseRecordingResources();

        setState((prev) => ({
            ...prev,
            isRecording: false,
            isProcessing: false,
            audioData: null,
            audioUrl: null,
            duration: 0,
            volume: 0,
            error: null,
        }));
    }, [releaseRecordingResources]);

    // ========== 卸载清理 ==========
    useEffect(() => {
        return () => {
            if (processorNodeRef.current) {
                processorNodeRef.current.onaudioprocess = null;
            }
            releaseRecordingResources();
            if (state.audioUrl) {
                URL.revokeObjectURL(state.audioUrl);
            }
        };
        // 仅卸载时执行
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const checkPermission = useCallback(async (): Promise<boolean> => {
        try {
            const requestUserMedia = getUserMedia();
            if (!requestUserMedia) return false;
            const stream = await requestUserMedia({
                audio: true,
            });
            stream.getTracks().forEach((t) => t.stop());
            return true;
        } catch {
            return false;
        }
    }, []);

    const getAudioData = useCallback((): Blob | null => {
        return state.audioData;
    }, [state.audioData]);

    const getAudioUrl = useCallback((): string | null => {
        return state.audioUrl;
    }, [state.audioUrl]);

    return {
        isRecording: state.isRecording,
        isProcessing: state.isProcessing,
        error: state.error,
        audioData: state.audioData,
        audioUrl: state.audioUrl,
        duration: state.duration,
        volume: state.volume,
        startRecording,
        stopRecording,
        stop,
        cancelRecording,
        drainPcmBlob,
        keepLastMs,
        checkPermission,
        getAudioData,
        getAudioUrl,
        isSupported: !microphoneUnavailableReason(),
    };
};
